import React, { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const SHEET_IN = 6;

function pxPerInFor(width) {
  return width < 640 ? 50 : 96; // true CSS-inch scale on desktop, shrunk to fit on mobile
}

function useResponsivePxPerIn() {
  const [px, setPx] = useState(() => pxPerInFor(typeof window !== "undefined" ? window.innerWidth : 1024));
  useEffect(() => {
    const onResize = () => setPx(pxPerInFor(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return px;
}

const DEFAULT_LAYOUT = {
  width_in: 2.625,
  height_in: 1.0,
  sticker1_left_in: 1.6875,
  sticker1_top_in: 1.0,
  sticker2_left_in: 1.6875,
  sticker2_top_in: 4.0,
  avery_name_font_pt: 11,
  avery_meta_font_pt: 9,
  sixbysix_name_font_pt: 11,
  sixbysix_meta_font_pt: 9,
  avery_line_gap_pt: 3,
  sixbysix_line_gap_pt: 3,
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function round(n) {
  return Math.round(n * 10000) / 10000;
}

export function SixBySixSettingsDialog({ open, onOpenChange, layout, onSave }) {
  const [draft, setDraft] = useState(layout || DEFAULT_LAYOUT);
  const [saving, setSaving] = useState(false);
  const dragRef = useRef(null);
  const canvasRef = useRef(null);
  const PX_PER_IN = useResponsivePxPerIn();
  const SHEET_PX = SHEET_IN * PX_PER_IN;

  useEffect(() => {
    if (open) setDraft(layout || DEFAULT_LAYOUT);
  }, [open, layout]);

  const clampPos = (left, top, w = draft.width_in, h = draft.height_in) => ({
    left: round(clamp(left, 0, SHEET_IN - w)),
    top: round(clamp(top, 0, SHEET_IN - h)),
  });

  const setSize = (patch) => {
    setDraft((d) => {
      const next = { ...d, ...patch };
      const p1 = clampPos(next.sticker1_left_in, next.sticker1_top_in, next.width_in, next.height_in);
      const p2 = clampPos(next.sticker2_left_in, next.sticker2_top_in, next.width_in, next.height_in);
      return { ...next, sticker1_left_in: p1.left, sticker1_top_in: p1.top, sticker2_left_in: p2.left, sticker2_top_in: p2.top };
    });
  };

  const setPos = (which, patch) => {
    setDraft((d) => {
      const left = patch.left_in !== undefined ? patch.left_in : d[`${which}_left_in`];
      const top = patch.top_in !== undefined ? patch.top_in : d[`${which}_top_in`];
      const p = clampPos(left, top);
      return { ...d, [`${which}_left_in`]: p.left, [`${which}_top_in`]: p.top };
    });
  };

  const onPointerDown = (which, e) => {
    e.preventDefault();
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = draft[`${which}_left_in`];
    const startTop = draft[`${which}_top_in`];
    dragRef.current = { which, startX, startY, startLeft, startTop, canvasRect };

    const onMove = (ev) => {
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setPos(dragRef.current.which, {
        left_in: dragRef.current.startLeft + dx / PX_PER_IN,
        top_in: dragRef.current.startTop + dy / PX_PER_IN,
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  const stickers = [
    { key: "sticker1", label: "1" },
    { key: "sticker2", label: "2" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid="sixbysix-settings-dialog">
        <DialogHeader>
          <DialogTitle>6&times;6&quot; sticker sheet settings</DialogTitle>
          <DialogDescription>
            Drag each sticker to position it on the sheet, or type exact inches. Shown at true size with a 1in grid for reference. Saved once — applies to every route printed with this style.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="mx-auto flex flex-shrink-0 flex-col items-center gap-2">
            <div
              ref={canvasRef}
              className="relative rounded-sm border border-border bg-surface"
              style={{
                width: SHEET_PX,
                height: SHEET_PX,
                backgroundImage:
                  `repeating-linear-gradient(to right, transparent, transparent ${PX_PER_IN - 1}px, #E4E7DE ${PX_PER_IN - 1}px, #E4E7DE ${PX_PER_IN}px),` +
                  `repeating-linear-gradient(to bottom, transparent, transparent ${PX_PER_IN - 1}px, #E4E7DE ${PX_PER_IN - 1}px, #E4E7DE ${PX_PER_IN}px)`,
              }}
              data-testid="sixbysix-settings-canvas"
            >
              <div
                className="pointer-events-none absolute left-0 right-0 border-t border-dashed border-text-muted/50"
                style={{ top: SHEET_PX / 2 }}
              />
            {stickers.map(({ key, label }) => (
              <div
                key={key}
                onPointerDown={(e) => onPointerDown(key, e)}
                className="absolute flex cursor-grab items-center justify-center overflow-hidden rounded-sm border-2 border-primary bg-primary/10 active:cursor-grabbing"
                style={{
                  left: draft[`${key}_left_in`] * PX_PER_IN,
                  top: draft[`${key}_top_in`] * PX_PER_IN,
                  width: draft.width_in * PX_PER_IN,
                  height: draft.height_in * PX_PER_IN,
                }}
                data-testid={`sixbysix-settings-${key}`}
              >
                <span className="pointer-events-none absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-white">
                  {label}
                </span>
                <div className="pointer-events-none flex flex-col items-start overflow-hidden px-1.5 text-left leading-tight">
                  <span className="font-bold text-text-main" style={{ fontSize: `${draft.sixbysix_name_font_pt}pt` }}>
                    Customer Name
                  </span>
                  <span className="text-text-muted" style={{ fontSize: `${draft.sixbysix_meta_font_pt}pt`, marginTop: `${draft.sixbysix_line_gap_pt}pt` }}>
                    Route- 0000, Tiffin- 00
                  </span>
                </div>
              </div>
            ))}
            </div>
            <p className="text-xs text-text-muted">6&times;6in sheet — shown at true size</p>
          </div>

          <div className="flex flex-1 flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Sticker width (in)</Label>
                <Input
                  type="number" step="0.0625" min="0.5" max={SHEET_IN} value={draft.width_in}
                  onChange={(e) => setSize({ width_in: clamp(parseFloat(e.target.value) || 0.5, 0.5, SHEET_IN) })}
                  data-testid="sixbysix-settings-width-input"
                />
              </div>
              <div>
                <Label className="text-xs">Sticker height (in)</Label>
                <Input
                  type="number" step="0.0625" min="0.5" max={SHEET_IN} value={draft.height_in}
                  onChange={(e) => setSize({ height_in: clamp(parseFloat(e.target.value) || 0.5, 0.5, SHEET_IN) })}
                  data-testid="sixbysix-settings-height-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-sm border border-border p-2">
              <p className="col-span-2 text-xs font-semibold text-text-main">Font size</p>
              <div>
                <Label className="text-xs">Name line (pt)</Label>
                <Input
                  type="number" step="0.5" min="4" max="40" value={draft.sixbysix_name_font_pt}
                  onChange={(e) => setDraft((d) => ({ ...d, sixbysix_name_font_pt: clamp(parseFloat(e.target.value) || 4, 4, 40) }))}
                  data-testid="sixbysix-settings-name-font-input"
                />
              </div>
              <div>
                <Label className="text-xs">Route/Tiffin line (pt)</Label>
                <Input
                  type="number" step="0.5" min="4" max="40" value={draft.sixbysix_meta_font_pt}
                  onChange={(e) => setDraft((d) => ({ ...d, sixbysix_meta_font_pt: clamp(parseFloat(e.target.value) || 4, 4, 40) }))}
                  data-testid="sixbysix-settings-meta-font-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Line spacing (pt)</Label>
                <Input
                  type="number" step="0.5" min="0" max="40" value={draft.sixbysix_line_gap_pt}
                  onChange={(e) => setDraft((d) => ({ ...d, sixbysix_line_gap_pt: clamp(parseFloat(e.target.value) || 0, 0, 40) }))}
                  data-testid="sixbysix-settings-line-gap-input"
                />
              </div>
            </div>
            {stickers.map(({ key, label }) => (
              <div key={key} className="grid grid-cols-2 gap-2 rounded-sm border border-border p-2">
                <p className="col-span-2 text-xs font-semibold text-text-main">Sticker {label} position</p>
                <div>
                  <Label className="text-xs">From left (in)</Label>
                  <Input
                    type="number" step="0.0625" min="0" max={SHEET_IN} value={draft[`${key}_left_in`]}
                    onChange={(e) => setPos(key, { left_in: parseFloat(e.target.value) || 0 })}
                    data-testid={`sixbysix-settings-${key}-left-input`}
                  />
                </div>
                <div>
                  <Label className="text-xs">From top (in)</Label>
                  <Input
                    type="number" step="0.0625" min="0" max={SHEET_IN} value={draft[`${key}_top_in`]}
                    onChange={(e) => setPos(key, { top_in: parseFloat(e.target.value) || 0 })}
                    data-testid={`sixbysix-settings-${key}-top-input`}
                  />
                </div>
              </div>
            ))}
            <Button
              variant="ghost" size="sm" className="w-fit"
              onClick={() => setDraft(DEFAULT_LAYOUT)}
              data-testid="sixbysix-settings-reset-button"
            >
              <RotateCcw size={14} strokeWidth={1.5} /> Reset to defaults
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="sixbysix-settings-cancel-button">Cancel</Button>
          <Button variant="accent" onClick={handleSave} disabled={saving} data-testid="sixbysix-settings-save-button">
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { DEFAULT_LAYOUT as SIXBYSIX_DEFAULT_LAYOUT };
